import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
/* eslint-disable-next-line no-unused-vars -- motion.div used in JSX (member expression not tracked by no-unused-vars) */
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axios";
import { getSocket } from "../socket";
import { useTheme } from "../ThemeContext";
import { useSidebar } from "../SidebarContext";
import { startPolling } from "../api/polling";
import Avatar from "./ui/Avatar";
import Spinner from "./ui/Spinner";
import LocationPicker from "./LocationPicker";
import { defaultLocation } from "../utils/locationUtils";
import { categories, CATEGORY_EMOJI, emptyFilters } from "../data/filters";

const CHAR_H = 29;
const LH = 1.4;
const LINE_H = CHAR_H * LH;

function LetterPullUp({ text, isHovered }) {
  return (
    <span className="inline-flex gap-px">
      {text.split("").map((char, i) => (
        <span
          key={i}
          style={{
            display: "inline-block",
            overflow: "hidden",
            height: `${LINE_H}px`,
            verticalAlign: "bottom",
          }}
        >
          <motion.span
            style={{
              display: "block",
              height: `${LINE_H}px`,
              lineHeight: `${LINE_H}px`,
              whiteSpace: "pre",
            }}
            animate={{ y: isHovered ? -LINE_H : 0 }}
            transition={{
              type: "spring",
              stiffness: 120,
              damping: 20,
              mass: 0.8,
              delay: isHovered ? i * 0.01 : (text.length - 1 - i) * 0.01,
            }}
          >
            <span style={{ display: "block", height: `${LINE_H}px` }}>
              {char}
            </span>
            <span style={{ display: "block", height: `${LINE_H}px` }}>
              {char}
            </span>
          </motion.span>
        </span>
      ))}
    </span>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const NOTIF_STYLE = {
  price_drop: {
    accent: "bg-blue-500",
    tint: "bg-blue-500/[0.04]",
    iconBg: "bg-blue-500/10",
    iconColor: "#3b82f6",
    badge: null,
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
  payment_received: {
    accent: "bg-emerald-500",
    tint: null,
    iconBg: "bg-emerald-500/10",
    iconColor: "#10b981",
    badge: null,
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
  order_confirmed: {
    accent: "bg-emerald-500",
    tint: null,
    iconBg: "bg-emerald-500/10",
    iconColor: "#10b981",
    badge: null,
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  delivery_confirmed: {
    accent: "bg-emerald-500",
    tint: null,
    iconBg: "bg-emerald-500/10",
    iconColor: "#10b981",
    badge: null,
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="1" y="3" width="15" height="13" />
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
  payment_failed: {
    accent: "bg-red-500",
    tint: "bg-red-500/[0.04]",
    iconBg: "bg-red-500/10",
    iconColor: "#ef4444",
    badge: {
      text: "Failed",
      cls: "bg-red-500/10 text-red-600 dark:text-red-400",
    },
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    ),
  },
  handoff_otp_ready: {
    accent: "bg-amber-500",
    tint: "bg-amber-500/[0.04]",
    iconBg: "bg-amber-500/10",
    iconColor: "#f59e0b",
    badge: {
      text: "Action",
      cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    },
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
  },
  payment_confirmation_needed: {
    accent: "bg-amber-500",
    tint: "bg-amber-500/[0.04]",
    iconBg: "bg-amber-500/10",
    iconColor: "#f59e0b",
    badge: {
      text: "Review",
      cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    },
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  dispute: {
    accent: "bg-red-500",
    tint: "bg-red-500/[0.04]",
    iconBg: "bg-red-500/10",
    iconColor: "#ef4444",
    badge: {
      text: "Dispute",
      cls: "bg-red-500/10 text-red-600 dark:text-red-400",
    },
    icon: (c) => (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
};
const NOTIF_STYLE_DEFAULT = {
  accent: "bg-zinc-400 dark:bg-zinc-500",
  tint: null,
  iconBg: "bg-zinc-100 dark:bg-zinc-800",
  iconColor: "var(--color-text-muted)",
  badge: null,
  icon: (c) => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

function SaleNotifRow({ notif, onDelete, onClick }) {
  const s = NOTIF_STYLE[notif.type] || NOTIF_STYLE_DEFAULT;
  const isPriceDrop = notif.type === "price_drop";
  const isChatRequest = notif.type === "chat_request";
  return (
    <div
      onClick={onClick}
      className={`relative flex items-center gap-3 pl-4 pr-3 py-3 cursor-pointer transition-colors border-b border-border last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-900 ${s.tint || ""} ${!notif.seen ? "bg-accent/[0.03]" : ""}`}
    >
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${notif.seen ? "bg-zinc-100 dark:bg-zinc-800" : s.iconBg}`}
      >
        {s.icon(notif.seen ? "var(--color-text-muted)" : s.iconColor)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-text truncate">
            {isChatRequest
              ? notif.buyerName || "Someone"
              : notif.itemTitle || "Item"}
          </p>
          {s.badge && (
            <span
              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.badge.cls}`}
            >
              {s.badge.text}
            </span>
          )}
        </div>
        <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">
          {isPriceDrop ? (
            <>
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                Price dropped
              </span>{" "}
              to ₹{Number(notif.price).toLocaleString("en-IN")}
            </>
          ) : isChatRequest ? (
            notif.message || "Sent you a chat request"
          ) : (
            notif.message || notif.type?.replace(/_/g, " ")
          )}
        </p>
        <p className="text-[11px] text-text-muted mt-1">
          {timeAgo(notif.createdAt)}
        </p>
      </div>
      {!notif.seen && (
        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-danger bg-danger/10 hover:bg-danger/20 cursor-pointer transition-colors"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function MsgNotifRow({ notif, onDelete, onClick }) {
  const isChatRequest = notif.type === "chat_request";
  return (
    <div
      onClick={onClick}
      className={`relative flex items-center gap-3 pl-4 pr-3 py-3 cursor-pointer transition-colors border-b border-border last:border-0 hover:bg-zinc-50 dark:hover:bg-zinc-900 ${!notif.seen ? "bg-accent/[0.03]" : ""}`}
    >
      <div
        className={`${isChatRequest ? "bg-blue-500" : "bg-indigo-500"} ${notif.seen ? "opacity-0" : "opacity-100"} absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full transition-opacity`}
      />
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${notif.seen ? "bg-zinc-100 dark:bg-zinc-800" : isChatRequest ? "bg-blue-500/10" : "bg-indigo-500/10"}`}
      >
        {isChatRequest ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={notif.seen ? "var(--color-text-muted)" : "#3b82f6"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={notif.seen ? "var(--color-text-muted)" : "#6366f1"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-text truncate">
            {notif.senderName || "Message"}
          </p>
          {isChatRequest && !notif.seen && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">
              New
            </span>
          )}
        </div>
        <p className="text-xs text-text-secondary mt-0.5 truncate leading-relaxed">
          {notif.lastMessage || notif.content || ""}
        </p>
        <p className="text-[11px] text-text-muted mt-1">
          {timeAgo(notif.createdAt)}
        </p>
      </div>
      {!notif.seen && (
        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-text-muted hover:text-danger hover:bg-danger/10 cursor-pointer transition-all"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleWithRay, theme, resetTheme } = useTheme();
  const { toggle: toggleSidebar, open: sidebarOpen } = useSidebar();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isLoggedIn = !!localStorage.getItem("token");

  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [saleNotifs, setSaleNotifs] = useState([]);
  const [msgNotifs, setMsgNotifs] = useState([]);
  const [unreadSales, setUnreadSales] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [activeTab, setActiveTab] = useState("sales");
  const [profileHoveredIdx, setProfileHoveredIdx] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [cartBlink, setCartBlink] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const [filters, setFilters] = useState(emptyFilters);
  const [loc, setLoc] = useState(defaultLocation);

  const notifRef = useRef(null);
  const profileRef = useRef(null);
  const notifOpenRef = useRef(false);
  const catScrollRef = useRef(null);

  function getDismissedReqIds() {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("dismissedChatReqs") || "[]"),
      );
    } catch {
      return new Set();
    }
  }
  function addDismissedReqId(id) {
    const s = getDismissedReqIds();
    s.add(id);
    localStorage.setItem("dismissedChatReqs", JSON.stringify([...s]));
  }

  const isHomePage = location.pathname === "/browse" || location.pathname === "/home";

  useEffect(() => {
    if (!isHomePage || mobileSearchOpen) return;
    const el = catScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isHomePage, mobileSearchOpen]);

  useEffect(() => {
    const handler = (e) => {
      const { filters: f, location: l } = e.detail || {};
      if (f) setFilters(f);
      if (l) setLoc(l);
    };
    window.addEventListener("navbar-filter-state", handler);
    return () => window.removeEventListener("navbar-filter-state", handler);
  }, []);

  useEffect(() => {
    const blink = () => {
      setCartBlink(true);
      setTimeout(() => setCartBlink(false), 1100);
    };
    window.addEventListener("cart-added", blink);
    return () => window.removeEventListener("cart-added", blink);
  }, []);

  const dispatchFilterChange = useCallback(
    (newFilters, newLoc) => {
      const f = newFilters !== undefined ? newFilters : filters;
      const l = newLoc !== undefined ? newLoc : loc;
      window.dispatchEvent(
        new CustomEvent("navbar-filter-change", {
          detail: { filters: f, location: l },
        }),
      );
    },
    [filters, loc],
  );

  useEffect(() => {
    notifOpenRef.current = notifOpen;
  }, [notifOpen]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchNotifs = async () => {
      if (notifOpenRef.current) return true;
      const results = await Promise.all([
        API.get("/notifications")
          .then((r) => ({ ok: true, data: r.data }))
          .catch(() => ({ ok: false, data: [] })),
        API.get("/messages/unread-count")
          .then((r) => ({ ok: true, data: r.data }))
          .catch(() => ({ ok: false, data: { count: 0 } })),
        API.get("/chat-requests")
          .then((r) => ({ ok: true, data: r.data }))
          .catch(() => ({ ok: false, data: { received: [] } })),
      ]);
      const pendingReqs = (results[2].data?.received || []).filter(
        (r) => r.status === "pending" && !getDismissedReqIds().has(r.id),
      ).length;
      setUnreadSales(
        Array.isArray(results[0].data) ? results[0].data.length : 0,
      );
      setUnreadMsgs((results[1].data?.count || 0) + pendingReqs);
      return results.every((r) => r.ok);
    };
    return startPolling(fetchNotifs, { baseMs: 30000 });
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const me = JSON.parse(localStorage.getItem("user") || "null");
    if (!me?.id) return;
    const socket = getSocket();
    if (!socket) return;

    const onNewMsg = (msg) => {
      if (String(msg.receiverId) !== String(me.id)) return;
      const activeKey = window.__activeConvoKey;
      const msgKeyA = `${msg.itemId}-${msg.senderId}`;
      const msgKeyB = `${msg.itemId}-${msg.receiverId}`;
      if (activeKey && (activeKey === msgKeyA || activeKey === msgKeyB)) return;
      setUnreadMsgs((prev) => prev + 1);
      setMsgNotifs((prev) => {
        if (prev.some((n) => n.id === msg.id)) return prev;
        return [
          {
            id: msg.id,
            senderId: msg.senderId,
            senderName: msg.senderName || "",
            senderAvatar: msg.senderAvatar || null,
            itemId: msg.itemId,
            itemTitle: msg.itemTitle || "",
            content: msg.content,
            createdAt: msg.createdAt,
          },
          ...prev,
        ];
      });
    };
    const onChatReq = (reqData) => {
      if (window.location.pathname === "/messages") return;
      setUnreadMsgs((prev) => prev + 1);
      if (!reqData?.id) return;
      setMsgNotifs((prev) => {
        const notif = {
          id: "req-" + reqData.id,
          type: "chat_request",
          senderId: reqData.sender?.id,
          senderName:
            `${reqData.sender?.firstName || ""} ${reqData.sender?.lastName || ""}`.trim(),
          senderAvatar: reqData.sender?.avatar || null,
          itemId: reqData.itemId,
          itemTitle: reqData.item?.title || "",
          content: reqData.message || "Sent you a chat request",
          createdAt: reqData.createdAt,
        };
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });
    };
    const onNewSale = (data) => {
      setUnreadSales((prev) => prev + 1);
      setSaleNotifs((prev) => [data, ...prev].slice(0, 20));
    };
    const onPriceDrop = (data) => {
      setUnreadSales((prev) => prev + 1);
      setSaleNotifs((prev) => [data, ...prev].slice(0, 20));
    };
    const onNewNotif = (data) => {
      if (data?.type === "chat_request") return;
      setUnreadSales((prev) => prev + 1);
      setSaleNotifs((prev) => [data, ...prev].slice(0, 20));
    };

    socket.on("new-message", onNewMsg);
    socket.on("new-chat-request", onChatReq);
    socket.on("new-sale", onNewSale);
    socket.on("price-drop", onPriceDrop);
    socket.on("new-notification", onNewNotif);
    return () => {
      socket.off("new-message", onNewMsg);
      socket.off("new-chat-request", onChatReq);
      socket.off("new-sale", onNewSale);
      socket.off("price-drop", onPriceDrop);
      socket.off("new-notification", onNewNotif);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target))
        setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openNotifs = async () => {
    if (notifOpen) {
      setNotifOpen(false);
      return;
    }
    setNotifOpen(true);
    setProfileOpen(false);
    try {
      const [saleRes, msgRes, reqRes] = await Promise.all([
        API.get("/notifications/all").catch(() => ({ data: [] })),
        API.get("/messages/unread").catch(() => ({ data: [] })),
        API.get("/chat-requests").catch(() => ({ data: { received: [] } })),
      ]);
      const pendingReqs = (reqRes.data?.received || []).filter(
        (r) => r.status === "pending" && !getDismissedReqIds().has(r.id),
      );
      const mergedMsgs = [
        ...(msgRes.data || []),
        ...pendingReqs.map((r) => ({
          id: "req-" + r.id,
          requestId: r.id,
          type: "chat_request",
          senderId: r.sender?.id,
          senderName:
            `${r.sender?.firstName || ""} ${r.sender?.lastName || ""}`.trim(),
          senderAvatar: r.sender?.avatar || null,
          itemId: r.itemId,
          itemTitle: r.item?.title || "",
          content: r.message || "Sent you a chat request",
          createdAt: r.createdAt,
        })),
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setSaleNotifs(saleRes.data || []);
      setMsgNotifs(mergedMsgs);
      const hasSales = (saleRes.data || []).length > 0;
      const hasMsgs = mergedMsgs.length > 0;
      if (hasSales && !hasMsgs) setActiveTab("sales");
      else if (hasMsgs && !hasSales) setActiveTab("messages");
      else setActiveTab("sales");
    } catch {
      setActiveTab("sales");
    }
  };

  const dismissMsgNotif = async (id) => {
    setMsgNotifs((prev) => prev.filter((n) => n.id !== id));
    if (typeof id === "number") {
      API.post("/messages/mark-all-read").catch(() => {});
      setUnreadMsgs((p) => Math.max(0, p - 1));
    } else setUnreadMsgs((p) => Math.max(0, p - 1));
  };

  const deleteNotif = async (id) => {
    try {
      await API.delete(`/notifications/${id}`);
      setSaleNotifs((p) => p.filter((n) => n.id !== id));
      setUnreadSales((c) => Math.max(0, c - 1));
    } catch { /* ignore delete failure */ }
  };

  const markAllSeen = async () => {
    try {
      await API.post("/notifications/mark-seen");
      setSaleNotifs((p) => p.map((n) => ({ ...n, seen: true })));
      setUnreadSales(0);
    } catch { /* ignore mark-seen failure */ }
  };

  const clearAllNotifs = async () => {
    try {
      await API.delete("/notifications/clear");
      setSaleNotifs([]);
      setUnreadSales(0);
    } catch { /* ignore */ }
  };

  const markAllMsgsSeen = async () => {
    try {
      await API.post("/messages/mark-all-read");
      setMsgNotifs((p) => p.map((n) => ({ ...n, seen: true })));
      setUnreadMsgs(0);
    } catch { /* ignore */ }
  };

  const clearAllMsgs = () => {
    setMsgNotifs([]);
    setUnreadMsgs(0);
  };

  const [signInPending, setSignInPending] = useState(false);
  const goToLogin = () => {
    if (signInPending) return;
    setSignInPending(true);
    setTimeout(() => navigate("/login"), 250);
  };

  const logout = () => {
    resetTheme();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/";
  };

  const totalUnread = unreadSales + unreadMsgs;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchVal.trim())
      navigate(`/browse?search=${encodeURIComponent(searchVal.trim())}`);
    else navigate("/browse");
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchVal.trim())
        navigate(`/browse?search=${encodeURIComponent(searchVal.trim())}`, {
          replace: true,
        });
      else if (new URLSearchParams(location.search).has("search"))
        navigate("/browse", { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [searchVal, location.search, navigate]);

  const activeCount = [
    filters.category,
    filters.subcategory,
    filters.sortPrice,
    filters.condition,
    filters.minPrice,
    filters.maxPrice,
    ...Object.values(filters.specs || {}).filter(Boolean),
  ].filter(Boolean).length;

  const handleCategoryClick = (cat) => {
    const current = filters.category
      ? filters.category
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : [];
    const next = current.includes(cat)
      ? current.filter((c) => c !== cat)
      : [...current, cat];
    const newFilters = {
      ...filters,
      category: next.join(","),
      subcategory: "",
      specs: {},
    };
    setFilters(newFilters);
    dispatchFilterChange(newFilters);
  };

  const handleLocChange = (newLoc) => {
    setLoc(newLoc);
    dispatchFilterChange(undefined, newLoc);
  };

  const filtersButton = (
    <button
      onClick={() => window.dispatchEvent(new Event("navbar-open-filters"))}
      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${
        activeCount > 0
          ? "bg-zinc-200 dark:bg-zinc-700 text-text border-border"
          : "bg-zinc-200 dark:bg-zinc-700 text-text border-border hover:bg-zinc-300 dark:hover:bg-zinc-600 hover:text-text"
      }`}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <line x1="4" y1="6" x2="20" y2="6" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="10" y1="18" x2="14" y2="18" />
      </svg>
      Filters
      {activeCount > 0 && (
        <span className="min-w-[16px] h-4 flex items-center justify-center rounded-full bg-accent text-white text-[9px] font-bold px-1">
          {activeCount}
        </span>
      )}
    </button>
  );

  return (
    <nav
      className={`fixed -top-px left-0 right-0 z-[70] backdrop-blur-xl border-b border-border/30 ${location.pathname === "/sellers" ? "bg-bg dark:bg-[#0C0A09]" : "bg-bg/80 dark:bg-[rgba(12,10,9,0.55)]"}`}
    >
      {mobileSearchOpen && isHomePage ? (
        /* ── Mobile expanded search ── */
        <div className="flex items-center gap-2 px-4 h-16 pt-2">
          <style>{`
            @keyframes mobileSearchIn { from { opacity:0; transform:scaleX(0.3); transform-origin:left } to { opacity:1; transform:scaleX(1); transform-origin:left } }
            @keyframes mobileLocIn { from { opacity:0; transform:translateX(8px) } to { opacity:1; transform:translateX(0) } }
          `}</style>
          <button
            onClick={() => {
              setMobileSearchOpen(false);
              setSearchVal("");
            }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-text-secondary hover:text-text cursor-pointer transition-colors flex-shrink-0"
            style={{ animation: "mobileSearchIn 0.25s ease-out" }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <form
            onSubmit={handleSearch}
            className="flex-1 relative"
            style={{ animation: "mobileSearchIn 0.3s ease-out" }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              autoFocus
              className="w-full pl-10 pr-4 py-2 bg-zinc-100 dark:bg-zinc-800/60 rounded-full text-sm text-text outline-none placeholder:text-text-muted border border-border focus:border-accent"
            />
          </form>
          <div
            className="flex-shrink-0"
            style={{ animation: "mobileLocIn 0.3s ease-out 0.15s both" }}
          >
            <LocationPicker location={loc} setLocation={handleLocChange} />
          </div>
        </div>
      ) : (
        <div className="h-16 flex items-start gap-3 px-4 md:px-5 pt-2">
          {/* Left: hamburger + logo */}
          <div className="flex items-center gap-3 md:gap-7 flex-shrink-0">
            <style>{`@keyframes navWipe{0%{clip-path:inset(0 0 0 0)}49.9%{clip-path:inset(0 0 0 100%)}50%{clip-path:inset(0 100% 0 0)}100%{clip-path:inset(0 0 0 0)}}.nav-hamburger .nav-line{animation:none}.nav-hamburger:hover .nav-line,.nav-hamburger .nav-line.playing{animation:navWipe 0.4s ease-in-out forwards}.nav-hamburger:hover .nav-line-d.playing,.nav-hamburger:hover .nav-line-d{animation-delay:0.04s}.nav-hamburger .nav-line-d.playing{animation-delay:0.04s}`}</style>
            <button
              onClick={toggleSidebar}
              onMouseLeave={(e) => {
                const lines = e.currentTarget.querySelectorAll(".nav-line");
                lines.forEach((l) => {
                  l.classList.remove("playing");
                  void l.offsetWidth;
                  l.classList.add("playing");
                });
                setTimeout(() => {
                  lines.forEach((l) => l.classList.remove("playing"));
                }, 440);
              }}
              className={`nav-hamburger w-10 h-10 flex-col items-start justify-center gap-[5px] rounded-lg cursor-pointer transition-colors md:-mt-2.5 px-3 md:-ml-1.5 ${!isLoggedIn ? "hidden md:flex" : "flex"}`}
            >
              <span
                className={`nav-line block h-[2px] rounded-full transition-colors ${sidebarOpen ? "bg-amber-600" : "bg-text-secondary"}`}
                style={{ width: "32px" }}
              />
              <span
                className={`nav-line nav-line-d block h-[2px] rounded-full transition-colors ${sidebarOpen ? "bg-amber-600" : "bg-text-secondary"}`}
                style={{ width: "26px" }}
              />
            </button>
            <Link
              to="/browse"
              className={`text-[1.8rem] md:text-[2.1rem] lg:text-[2.3rem] font-bold text-text tracking-tight nav-logo-tablet -translate-y-1 ${!isLoggedIn ? "block" : "hidden sm:block"}`}
              style={{ fontFamily: "'Brafesuit', var(--font-sans)", letterSpacing: '0.04em' }}
            >
              <span className="text-amber-600 dark:text-white">UniHaul</span><span className="text-accent">.</span>
            </Link>
            {isHomePage && (
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="sm:hidden w-9 h-9 flex items-center justify-center rounded-lg text-text-secondary hover:text-text cursor-pointer transition-colors flex-shrink-0"
                style={{ marginTop: "2px" }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>
          {/* Center: location + search bar (desktop) — home page only */}
          <div className="hidden sm:flex items-center gap-3 ml-auto mr-4">
            {isHomePage && (
              <div className="mt-0.5">
                <LocationPicker location={loc} setLocation={handleLocChange} />
              </div>
            )}
            {isHomePage && (
              <form
                onSubmit={handleSearch}
                className={`relative shrink-0 transition-all duration-300 ease-out ${searchFocused || searchVal ? "sm:w-[220px] lg:w-[340px]" : "sm:w-[180px] lg:w-[260px]"}`}
              >
                <div className="search-wrap relative">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchVal}
                    onChange={(e) => setSearchVal(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() =>
                      setTimeout(() => setSearchFocused(false), 150)
                    }
                    className="w-full pl-10 pr-4 py-2 bg-zinc-100 dark:bg-zinc-800/60 rounded-full text-sm text-text outline-none transition-all placeholder:text-text-muted border border-border hover:border-border-hover focus:border-accent focus:shadow-[0_0_0_2px_rgba(0,0,0,0.08)] dark:focus:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]"
                  />
                </div>
              </form>
            )}
          </div>

          {/* Right: notif + cart + profile */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            {isLoggedIn && (
              <>
                <div ref={notifRef} className="relative">
                  <button
                    onClick={openNotifs}
                    className={`relative h-9 w-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors mt-px ${notifOpen ? "text-amber-600" : "text-text-secondary hover:text-amber-600"}`}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    {!notifOpen && totalUnread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-white text-[10px] font-bold px-1">
                        {totalUnread > 99 ? "99+" : totalUnread}
                      </span>
                    )}
                  </button>
                  <AnimatePresence>
                  {notifOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className="fixed sm:absolute right-4 sm:right-[-4px] top-[52px] sm:top-full sm:mt-2 w-[min(340px,calc(100vw-32px))] sm:w-80 bg-surface border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden z-50"
                    >
                      <div className="flex border-b border-border">
                        <button
                          onClick={() => setActiveTab("sales")}
                          className={`flex-1 py-2.5 text-xs font-semibold cursor-pointer transition-colors flex items-center justify-center gap-1.5 ${activeTab === "sales" ? "text-accent border-b-2 border-accent" : "text-text-secondary hover:text-text"}`}
                        >
                          Sales & Alerts
                          {activeTab !== "sales" && unreadSales > 0 && (
                            <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-white text-[10px] font-bold px-1">
                              {unreadSales > 99 ? "99+" : unreadSales}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => setActiveTab("messages")}
                          className={`flex-1 py-2.5 text-xs font-semibold cursor-pointer transition-colors flex items-center justify-center gap-1.5 ${activeTab === "messages" ? "text-accent border-b-2 border-accent" : "text-text-secondary hover:text-text"}`}
                        >
                          Messages
                          {activeTab !== "messages" && unreadMsgs > 0 && (
                            <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-white text-[10px] font-bold px-1">
                              {unreadMsgs > 99 ? "99+" : unreadMsgs}
                            </span>
                          )}
                        </button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {(activeTab === "sales" && saleNotifs.length > 0) && (
                          <div className="flex items-center justify-between px-4 py-1.5 border-b border-border/30">
                            {unreadSales > 0 && (
                              <button onClick={markAllSeen} className="text-[11px] font-medium text-accent hover:text-accent/80 cursor-pointer transition-colors">
                                Mark all read
                              </button>
                            )}
                            <button onClick={clearAllNotifs} className="text-[11px] font-medium text-danger hover:text-danger/80 cursor-pointer transition-colors">
                              Clear all
                            </button>
                          </div>
                        )}
                        {(activeTab === "messages" && msgNotifs.length > 0) && (
                          <div className="flex items-center justify-between px-4 py-1.5 border-b border-border/30">
                            {unreadMsgs > 0 && (
                              <button onClick={markAllMsgsSeen} className="text-[11px] font-medium text-accent hover:text-accent/80 cursor-pointer transition-colors">
                                Mark all read
                              </button>
                            )}
                            <button onClick={clearAllMsgs} className="text-[11px] font-medium text-danger hover:text-danger/80 cursor-pointer transition-colors">
                              Clear all
                            </button>
                          </div>
                        )}
                        {activeTab === "sales" ? (
                          saleNotifs.length === 0 ? (
                            <div className="text-center py-6 px-4">
                              <img
                                src="/assets/push-notifications.svg"
                                alt=""
                                className="w-28 h-28 object-contain mx-auto mb-2 opacity-80"
                              />
                              <p className="text-xs text-text-muted font-medium">
                                No notifications yet
                              </p>
                            </div>
                          ) : (
                            <>
                              {saleNotifs.map((n) => (
                                <SaleNotifRow
                                  key={n.id}
                                  notif={n}
                                  onDelete={() => deleteNotif(n.id)}
                                  onClick={() => {
                                    deleteNotif(n.id);
                                    setNotifOpen(false);
                                    if (n.type === "chat_request")
                                      navigate("/messages");
                                    else if (
                                      [
                                        "payment_confirmation_needed",
                                        "dispute",
                                      ].includes(n.type)
                                    )
                                      navigate("/orders");
                                    else if (
                                      [
                                        "payment_received",
                                        "order_confirmed",
                                        "payment_failed",
                                        "delivery_confirmed",
                                        "handoff_otp_ready",
                                      ].includes(n.type)
                                    )
                                      navigate("/transactions");
                                    else if (n.itemId)
                                      navigate(`/items/${n.itemId}`);
                                  }}
                                />
                              ))}
                            </>
                          )
                        ) : msgNotifs.length === 0 ? (
                          <div className="text-center py-6 px-4">
                            <img
                              src="/assets/message-notification.png"
                              alt=""
                              className="w-28 h-28 object-contain mx-auto mb-2 opacity-80"
                            />
                            <p className="text-xs text-text-muted font-medium">
                              No messages yet
                            </p>
                          </div>
                        ) : (
                          msgNotifs.map((m, i) => (
                            <MsgNotifRow
                              key={m.id || i}
                              notif={m}
                              onDelete={() => {
                                setMsgNotifs((p) =>
                                  p.filter((n) => n.id !== m.id),
                                );
                              }}
                              onClick={() => {
                                if (m.type === "chat_request" && m.requestId)
                                  addDismissedReqId(m.requestId);
                                dismissMsgNotif(m.id);
                                setNotifOpen(false);
                                navigate("/messages", {
                                  state: {
                                    notification: {
                                      senderId: m.senderId,
                                      itemId: m.itemId,
                                      requestId: m.requestId,
                                      isChatRequest: m.type === "chat_request",
                                    },
                                  },
                                });
                              }}
                            />
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>

                {(() => {
                  const isCartPage = location.pathname === "/cart";
                  return (
                    <Link
                      to="/cart"
                      className={`relative h-9 w-9 flex items-center justify-center rounded-lg transition-colors ${isCartPage ? "text-amber-600" : "text-text-secondary hover:text-amber-600"}`}
                      style={
                        cartBlink
                          ? { animation: "cartBlink 1s ease 1" }
                          : undefined
                      }
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 50 60"
                        fill="currentColor"
                        {...(isCartPage
                          ? {
                              stroke: "currentColor",
                              strokeWidth: "0.5",
                              paintOrder: "stroke fill",
                            }
                          : {})}
                      >
                        <path d="M21.9 33.6a1.5 1.5 0 013 0 1.5 1.5 0 01-3 0zM46.8 19v29.2a7.3 7.3 0 01-7.3 7.3H7.3A7.3 7.3 0 010 48.2V19a1.5 1.5 0 011.5-1.5h10.2v-5.8a11.7 11.7 0 0123.4 0v5.8h10.2a1.5 1.5 0 011.5 1.5zm-32.2-1.5h17.6v-5.8a8.8 8.8 0 00-17.6 0v5.8zM3 20.5a20.5 20.5 0 0040.8 0zm40.9 27.7V30.3a23.4 23.4 0 01-41 0v17.9a4.4 4.4 0 004.4 4.4h32.2a4.4 4.4 0 004.4-4.4z" />
                      </svg>
                    </Link>
                  );
                })()}
              </>
            )}

            {isLoggedIn ? (
              <div ref={profileRef} className="relative">
                <button
                  onClick={() => {
                    setProfileOpen(!profileOpen);
                    setNotifOpen(false);
                  }}
                  className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 cursor-pointer transition-colors"
                >
                  <Avatar
                    src={user?.avatar}
                    name={`${user?.firstName || ""} ${user?.lastName || ""}`}
                    size="sm"
                  />
                </button>
                {profileOpen && (
                  <div
                    className="absolute right-[-10px] top-full mt-2 w-52 overflow-hidden animate-scale-in z-[60]"
                    style={{
                      backgroundColor: theme === "dark" ? "#292524" : "#EDECEB",
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.4,
                        delay: 0,
                      }}
                    >
                      <div
                        className="px-5 py-4 border-b"
                        style={{
                          borderColor: theme === "dark" ? "#44403c" : "#d6d3d1",
                        }}
                      >
                        <p className="text-sm font-semibold text-text">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-text-muted truncate">
                          {user?.email}
                        </p>
                      </div>
                    </motion.div>
                    <div className="py-2">
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.4,
                          delay: 0.06,
                        }}
                      >
                        <Link
                          to="/messages"
                          onMouseEnter={() => setProfileHoveredIdx(0)}
                          onMouseLeave={() => setProfileHoveredIdx(null)}
                          className={`flex items-center px-5 py-2 transition-colors ${location.pathname.startsWith("/messages") ? "text-amber-600" : "text-text-secondary hover:text-text"}`}
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontWeight: 550,
                            fontSize: "21px",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          <LetterPullUp
                            text="Messages"
                            isHovered={profileHoveredIdx === 0}
                          />
                        </Link>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.4,
                          delay: 0.12,
                        }}
                      >
                        <Link
                          to="/watching"
                          onMouseEnter={() => setProfileHoveredIdx(1)}
                          onMouseLeave={() => setProfileHoveredIdx(null)}
                          className={`flex items-center px-5 py-2 transition-colors ${location.pathname.startsWith("/watching") ? "text-amber-600" : "text-text-secondary hover:text-text"}`}
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontWeight: 550,
                            fontSize: "21px",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          <LetterPullUp
                            text="Watching"
                            isHovered={profileHoveredIdx === 1}
                          />
                        </Link>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.4,
                          delay: 0.18,
                        }}
                      >
                        <Link
                          to="/settings"
                          onMouseEnter={() => setProfileHoveredIdx(2)}
                          onMouseLeave={() => setProfileHoveredIdx(null)}
                          className={`flex items-center px-5 py-2 transition-colors ${location.pathname.startsWith("/settings") ? "text-amber-600" : "text-text-secondary hover:text-text"}`}
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontWeight: 550,
                            fontSize: "21px",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          <LetterPullUp
                            text="Settings"
                            isHovered={profileHoveredIdx === 2}
                          />
                        </Link>
                      </motion.div>
                      {user?.role === "admin" && (
                        <motion.div
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            type: "spring",
                            bounce: 0.2,
                            duration: 0.4,
                            delay: 0.24,
                          }}
                        >
                          <Link
                            to="/admin"
                            onMouseEnter={() => setProfileHoveredIdx(3)}
                            onMouseLeave={() => setProfileHoveredIdx(null)}
                            className={`flex items-center px-5 py-2 transition-colors ${location.pathname.startsWith("/admin") ? "text-amber-600" : "text-text-secondary hover:text-text"}`}
                            style={{
                              fontFamily: "'Instrument Serif', serif",
                              fontWeight: 550,
                              fontSize: "21px",
                              letterSpacing: "-0.01em",
                            }}
                          >
                            <LetterPullUp
                              text="Admin"
                              isHovered={profileHoveredIdx === 3}
                            />
                          </Link>
                        </motion.div>
                      )}
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.4,
                          delay: 0.3,
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWithRay();
                          }}
                          onMouseEnter={() => setProfileHoveredIdx(4)}
                          onMouseLeave={() => setProfileHoveredIdx(null)}
                          className="w-full flex items-center justify-between px-5 py-2 cursor-pointer transition-colors"
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontWeight: 550,
                            fontSize: "21px",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          <span className="text-text-secondary hover:text-text">
                            {theme === "dark" ? "Light mode" : "Dark mode"}
                          </span>
                          <span
                            className={`relative shrink-0 w-[42px] h-[24px] rounded-full transition-colors duration-200 ${theme === "dark" ? "border-0" : "border border-zinc-300"}`}
                            style={{
                              backgroundColor:
                                theme === "dark"
                                  ? "var(--accent)"
                                  : "var(--surface-raised)",
                            }}
                          >
                            <span
                              className={`absolute left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-200 ${theme === "dark" ? "top-[3px]" : "top-[2px]"}`}
                              style={{
                                transform:
                                  theme === "dark"
                                    ? "translateX(20px)"
                                    : "translateX(0)",
                              }}
                            />
                          </span>
                        </button>
                      </motion.div>
                    </div>
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.4,
                        delay: 0.36,
                      }}
                    >
                      <div
                        className="border-t py-2"
                        style={{
                          borderColor: theme === "dark" ? "#44403c" : "#d6d3d1",
                        }}
                      >
                        <button
                          onClick={() => {
                            setProfileOpen(false);
                            logout();
                          }}
                          onMouseEnter={() => setProfileHoveredIdx(5)}
                          onMouseLeave={() => setProfileHoveredIdx(null)}
                          className="w-full flex items-center px-5 py-2 text-danger hover:text-danger cursor-pointer transition-colors"
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontWeight: 550,
                            fontSize: "21px",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          <LetterPullUp
                            text="Sign out"
                            isHovered={profileHoveredIdx === 5}
                          />
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={goToLogin}
                disabled={signInPending}
                aria-busy={signInPending}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-accent-hover transition-colors disabled:cursor-wait disabled:opacity-70"
              >
                {signInPending ? (
                  <Spinner size={16} color="text-white" />
                ) : (
                  "Sign in"
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Row 2: category chips + location picker (home page only) */}
      {isHomePage && !mobileSearchOpen && (
        <div
          className={`flex items-center gap-1.5 pb-2 pl-4 pr-4 xl:pl-[270px] transition-[padding] duration-300 ease-out ${
            sidebarOpen ? "lg:pl-[272px]" : "lg:pl-4"
          }`}
        >
          {/* Mobile: location picker — stays fixed */}
          <div className="sm:hidden flex-shrink-0 mr-1">
            <LocationPicker location={loc} setLocation={handleLocChange} />
          </div>
          <div className="sm:hidden w-px h-5 bg-border flex-shrink-0" />
          {/* Category chips — scrollable; desktop keeps Filters inline after last chip */}
          <div
            className={`min-w-0 flex-1 transition-[padding] duration-300 ease-out ${
              sidebarOpen ? "xl:pl-[110px]" : ""
            }`}
          >
            <div
              ref={catScrollRef}
              className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pr-2"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all border ${
                    filters.category &&
                    filters.category
                      .split(",")
                      .map((c) => c.trim())
                      .includes(cat)
                      ? "bg-accent text-white border-accent"
                      : "bg-zinc-100 dark:bg-zinc-800 text-text-secondary border-transparent hover:bg-accent/10 hover:text-accent"
                  }`}
                >
                  <span className="whitespace-nowrap">{cat}</span>
                </button>
              ))}
              {/* Filters — desktop only: sits right after the last category chip */}
              <div className="hidden lg:contents">{filtersButton}</div>
            </div>
          </div>
          {/* Filters — mobile/tablet: pinned at the right edge */}
          <div className="hidden sm:block lg:hidden w-px h-5 bg-border flex-shrink-0 mx-0.5" />
          <div className="lg:hidden flex-shrink-0">{filtersButton}</div>
        </div>
      )}
    </nav>
  );
}
